<template>
  <div>
    <table
      v-data-table="{
        paging: false,
        searching: true,
        info: false,
        columnDefs: [{ targets: [-1, -2, -3], orderable: false }],
        order: [[0, 'asc']],
        tempLength: teamMembers.length,
      }"
      class="table table-hover"
      data-test="gdt-table"
    >
      <thead>
        <tr>
          <th></th>
          <th>First Name</th>
          <th>Last Name</th>
          <th class="col-sm-2">Role</th>
          <th>Solo</th>
          <th>Viewpoints</th>
          <th>Develop</th>
          <th class="col-sm-3">Team Pathways</th>
          <th>Share?</th>
          <th>Organisation</th>
          <th>Subscription</th>
          <th>Licence</th>
        </tr>
      </thead>
      <tbody class="table-body">
        <tr v-for="member in teamMembers" :key="member.userDetails.id" data-test="gdt-row">
          <td style="padding-top: 8px">
            <button
              class="btn"
              aria-label="open more details"
              :class="isExpanded(member) ? 'btn-collapse-sm' : 'btn-expand-sm'"
              @click="toggleExpansion(member)"
              data-test="gdt-expand-button"
              type="button"
            ></button>
          </td>
          <!-- First name Column -->
          <td data-test="gdt-first-name">
            <a :href="`/admin/user/details/${member.userDetails.id}`">{{
              member.userDetails.firstName
            }}</a>
          </td>
          <!-- Last name Column -->
          <td data-test="gdt-last-name">
            <a :href="`/admin/user/details/${member.userDetails.id}`">{{
              member.userDetails.lastName
            }}</a>
          </td>
          <!-- Roles Column -->
          <td class="col-sm-2">
            <div class="role-data" data-test="gdt-role-tags">
              <div class="role-tags">
                <button
                  class="btn btn-default btn-sm"
                  :class="[isAdmin(member) && isWatcher(member) ? '' : 'dropdown-toggle']"
                  data-toggle="dropdown"
                  aria-haspopup="true"
                  aria-expanded="false"
                  data-test="gdt-member-tag"
                  type="button"
                >
                  Member
                </button>
                <ul
                  v-if="rolesNotAssignedToMember(member).length"
                  class="dropdown-menu user-roles text-sm"
                  data-test="gdt-member-dropdown"
                >
                  <li
                    v-for="role in rolesNotAssignedToMember(member)"
                    :key="`${member.userDetails.lastName}(${role})`"
                    @click="openRoleModal(member, role)"
                    class="list-items"
                    :data-test="`gdt-add-${role.toLowerCase()}`"
                    @keydown.enter="openRoleModal(member, role)"
                  >
                    <span
                      ><img
                        src="../../../assets/button-icons/plus-icon-disabled.svg"
                        :alt="`add ${role.toLowerCase()}`"
                    /></span>
                    <p class="text-sm" style="margin-bottom: 0">
                      {{ role === "admin" ? "team lead" : role }}
                    </p>
                  </li>
                </ul>
              </div>
              <div
                v-for="role in member.roles"
                :key="`${member.userDetails.id} (${role})`"
                class="role-tags"
              >
                <button
                  type="button"
                  class="btn btn-primary btn-sm dropdown-toggle"
                  data-toggle="dropdown"
                  aria-haspopup="true"
                  aria-expanded="false"
                  :data-test="`gdt-${role.role}-tag`"
                >
                  {{ role.role === "admin" ? "Team Lead" : "Watcher" }}
                </button>
                <ul
                  class="dropdown-menu user-roles text-sm"
                  :data-test="`gdt-${role.role}-dropdown`"
                >
                  <li
                    @click="openRoleModal(member, role.role)"
                    :data-test="`gdt-remove-${role.role}`"
                  >
                    Remove role
                  </li>
                </ul>
              </div>
            </div>
          </td>
          <!-- Solo Column -->
          <td class="flex-col">
            <a
              v-if="member.solo.activeReport.linkText !== 'no solo'"
              :href="member.solo.activeReport.linkUrl"
              style="font-weight: bold"
              data-test="gdt-current-solo"
              >{{ member.solo.activeReport.linkText }}</a
            >
            <template v-if="member.solo.otherReports.length > 0 && isExpanded(member)">
              <a
                v-for="report in member.solo.otherReports"
                :href="report.linkUrl"
                :key="report.linkText"
                data-test="gdt-previous-solo"
                >{{ report.linkText }}</a
              >
            </template>
          </td>
          <!-- Viewpoints Column -->
          <td>
            <a
              :href="`/viewpoints/view/${member.userDetails.id}`"
              v-if="member.viewpoints.activeReport.linkText !== 'no viewpoints'"
              data-test="gdt-viewpoints"
            >
              {{ member.viewpoints.activeReport.linkText }}
            </a>
          </td>
          <!-- Develop Column -->
          <td class="col-sm-2">
            <div v-if="!!member.develop?.currentStrengthProgress" data-test="gdt-current-develop">
              <div style="text-decoration: underline">
                {{ member.develop.currentStrengthProgress.strengthItemDisplayTitle }}:
              </div>
              <div v-if="!!member.develop.currentStrengthProgress?.lastDayIndex" class="text-muted">
                Completed Day {{ member.develop.currentStrengthProgress.lastCompletedDayIndex }}
              </div>
              <div v-else class="text-muted">Not started</div>
              <!-- Not started if date is null or less than 1. 'Completed' Day X -->
              <!-- Last activity: dd/mm/yyyy -->
              <div
                v-if="!!member.develop.currentStrengthProgress?.lastReadActionTime"
                class="text-muted"
                style="margin-top: 10px"
              >
                Last activity:
                {{ displayDate(member.develop.currentStrengthProgress.lastReadActionTime) }}
              </div>
              <div class="text-muted"></div>
            </div>
            <template v-if="member.develop.otherProgress && isExpanded(member)">
              <div
                v-for="progress in member.develop.otherProgress"
                :key="progress.strengthItemId"
                data-test="gdt-previous-develop"
              >
                {{ progress.strengthItemDisplayTitle }} : Day
                {{ progress.lastCompletedDayIndex }}
                <span>
                  {{ displayDate(progress.lastReadActionTime) }}
                </span>
              </div>
            </template>
          </td>
          <!-- Team pathways Column -->
          <td class="col-sm-3">
            <template v-if="member.pathways.currentPathwaysInProgress">
              <p
                v-if="member.pathways.currentPathwaysInProgress.length > 0"
                style="text-decoration: underline; margin-bottom: 0"
              >
                Current progress:
              </p>
              <div
                v-for="(currentPathways, index) in member.pathways.currentPathwaysInProgress"
                :key="currentPathways.cmsStepUid + index"
                style="display: flex; align-items: center; margin-bottom: 1rem"
                data-test="gdt-current-tpathway"
              >
                <TeamMemberPathway
                  v-if="currentPathways.teamId === teamId"
                  :model="currentPathways"
                ></TeamMemberPathway>
              </div>
            </template>
            <template v-if="member.pathways.otherProgress && isExpanded(member)">
              <p
                v-if="member.pathways.otherProgress.length > 0"
                style="text-decoration: underline; margin-bottom: 0"
              >
                Other progress:
              </p>
              <div
                v-for="(otherPathways, index) in member.pathways.otherProgress"
                :key="otherPathways.cmsStepUid + index"
                style="display: flex; align-items: center; margin-bottom: 1rem"
                data-test="gdt-previous-tpathway"
              >
                <TeamMemberPathway
                  v-if="otherPathways.teamId === teamId"
                  :model="otherPathways"
                ></TeamMemberPathway>
              </div>
            </template>
          </td>
          <!-- Share reports Column -->
          <td>
            <span
              class="glyphicon glyphicon-ok"
              v-if="member.allowTeamMembersToReadMyReports"
              data-test="gdt-share"
            ></span>
          </td>
          <!-- Org Column -->
          <td>
            <a
              :href="`/organisation/details/${member.userDetails.organisation?.id}`"
              data-test="gdt-org-name"
              >{{ member.userDetails.organisation?.name }}</a
            >
          </td>

          <!-- Subscription Column -->
          <td data-test="gdt-member-sub">
            <div v-if="memberHasConflictingSubscriptions(member)">
              <strong class="text-danger">Conflict between </strong>
              <router-link :to="`/admin/group/details/${member.subscriptions[0].group.id}`"
                >'{{ member.subscriptions[0].group.name }}'</router-link
              >
              <span
                :key="subscription.group.id"
                v-for="subscription in member.subscriptions.slice(1)"
              >
                <strong class="text-danger"> and </strong>
                <router-link :to="`/admin/group/details/${subscription.group.id}`"
                  >'{{ subscription.group.name }}'</router-link
                >
              </span>
            </div>

            <div v-else>
              <span>----</span>
            </div>
          </td>
          <td>
            <button
              v-if="hasActiveLicence(member)"
              class="btn btn-default"
              @click="toggleModal(member.licence)"
              data-test="gdt-delete-licence"
              type="button"
            >
              Delete Licence
            </button>
            <span v-else>
              <button
                @click="toggleAssignLicenceModal(member)"
                class="btn btn-primary"
                type="button"
                data-test="gdt-assign-licence"
              >
                Assign Licence
              </button>
            </span>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script setup lang="ts">
import moment from "dayjs";
import { ref, watchEffect } from "vue";
import TeamMemberPathway from "../../group-member-table/team-member-pathway.vue";
import { LicenceApiModel, TeamCurrentMemberApiModel } from "../../../api-models";
import { GroupUserRoles } from "./group-user-roles.enum";

const props = defineProps<{ teamMembers: TeamCurrentMemberApiModel[]; teamId: string }>();

const emit = defineEmits([
  "open-role-modal",
  "toggle-licence-modal",
  "toggle-assign-licence-modal",
]);

const expandedMemberRows = ref<string[]>([]);

const roles = Object.keys(GroupUserRoles).filter((r) => {
  return r !== "Member";
});

const toggleExpansion = (member: TeamCurrentMemberApiModel): void => {
  const { id } = member.userDetails;
  if (!expandedMemberRows.value.includes(id)) {
    expandedMemberRows.value.push(id);
  } else {
    expandedMemberRows.value = expandedMemberRows.value.filter((r) => {
      return r !== id;
    });
  }
};

const isExpanded = (member): boolean => {
  return expandedMemberRows.value.includes(member.userDetails.id);
};

const isAdmin = (member: TeamCurrentMemberApiModel): boolean => {
  const admin = member.roles.filter((role) => {
    return role.role === GroupUserRoles.Admin && role.id === member.userDetails.id;
  });
  return admin.length > 0;
};

const isWatcher = (member: TeamCurrentMemberApiModel): boolean => {
  const watcher = member.roles.filter((role) => {
    return role.role === GroupUserRoles.Watcher && role.id === member.userDetails.id;
  });
  return watcher.length > 0;
};

const rolesNotAssignedToMember = (member: TeamCurrentMemberApiModel): string[] => {
  const allPossibleRoles = roles.map((r) => r.toLowerCase());
  const teamRoles = member.roles
    .filter((role) => {
      return role.groupId === props.teamId && role.id === member.userDetails.id;
    })
    .map((role) => role.role);

  return allPossibleRoles.filter((r) => {
    return !teamRoles.includes(r);
  });
};

const hasActiveLicence = (member: TeamCurrentMemberApiModel): boolean => {
  const autoLicenceId = "00000000-0000-0000-0000-000000000000";
  return member.licence.id !== autoLicenceId;
};

const displayDate = (dateString: string): string => {
  return dateString ? moment(dateString).format("DD/MM/YYYY") : "";
};

const openRoleModal = (member: TeamCurrentMemberApiModel, role: string): void => {
  emit("open-role-modal", member, role);
};

const toggleModal = (member: LicenceApiModel): void => {
  emit("toggle-licence-modal", member);
};

const memberHasConflictingSubscriptions = (member: TeamCurrentMemberApiModel): boolean => {
  return member.subscriptions.length > 1;
};

const memberHasNoSubscriptions = (member: TeamCurrentMemberApiModel): boolean =>
  member.subscriptions.length < 1;

const toggleAssignLicenceModal = (member: TeamCurrentMemberApiModel): void => {
  emit("toggle-assign-licence-modal", member);
};
</script>

<style scoped lang="less">
.table-body {
  td {
    padding-top: 14px;
  }
}

.role-data,
.role-tags {
  display: flex;
  flex-direction: column;
}

.role-data {
  gap: 1rem;
}

.role-tags {
  max-width: 100px;
  position: relative;
}

.font-size-small {
  font-size: 14px;
}

.dropdown-slot-text {
  margin-bottom: 0;
}

.flex-col {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.list-item {
  display: flex;
  gap: 1rem;
  align-items: center;
}
</style>

<style lang="less">
@import (reference) "../../../styles/bootstrap/variables";
.role-tags > .dropdown-backdrop {
  display: none;
}

.dropdown-menu.user-roles {
  position: absolute;
  min-width: 100px;
  border-radius: 15px;
  overflow: hidden;
  z-index: 1;

  li {
    overflow: hidden;
    padding-left: 0.5rem;
    padding-block: 0.25rem;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 1rem;
    color: @form-text-color;
    &:hover {
      cursor: pointer;
      background-color: @dropdown-hover-color;
      overflow: hidden;
    }
  }
}
</style>
